/**
 * @module components/canvas3d/Canvas3DView
 * @summary The 3D mode surface: a full-bleed canvas owned by `engine3d`, plus
 *   the floating HUD that drives it.
 * @description
 *   Mirrors `CanvasView`'s contract exactly — React renders this DOM once and
 *   the engine owns the canvas from then on, so the render loop is never
 *   disturbed by a re-render. Only the HUD is reactive, and it updates on
 *   selection/document events plus a slow poll for the statistics.
 *
 *   This component is mounted OVER the 2D editor rather than in place of it, so
 *   the 2D engine keeps its state, its autosave and its selection while you are
 *   looking at the plant in 3D.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { engine3d, type Selection3D, type View3DStats } from '../../engine3d/Engine3D';
import { engine } from '../../engine/CanvasEngine';
import type { ViewPreset } from '../../engine3d/camera';
import type { BendStyle, FlowMedium, Level, PortKey } from '../../types';
import type { AxisMode } from '../../engine3d/pipes3';
import { MODEL_CHOICES } from '../../engine3d/models';
import { FLOW_INK } from '../../engine3d/flow';
import { FLOW_NAMES } from '../../engine/streams';
import {
  FG_COLOUR,
  FG_START_TYPES,
  feedOf,
  isFgStart,
  shortMaterialName,
} from '../../engine/formulation';
import { formatRate } from '../../engine/balance';
import { FeedEditor } from '../formulation/FeedEditor';
import '../../styles/view3d.css';
import '../../styles/formulation.css';

const PRESETS: { id: ViewPreset; label: string; title: string }[] = [
  { id: 'iso', label: 'Iso', title: 'True isometric — parallel runs stay parallel' },
  { id: 'isoSW', label: 'SW', title: 'Three-quarter view from the south-west' },
  { id: 'isoNE', label: 'NE', title: 'Three-quarter view from the north-east' },
  { id: 'top', label: 'Plan', title: 'Straight down — the plant layout' },
  { id: 'front', label: 'Front', title: 'Elevation along the line' },
  { id: 'side', label: 'Side', title: 'Elevation across the line' },
];

/** The same figure format the dimension lines use, for the read-out. */
function formatMm(mm: number): string {
  if (mm >= 1000) {
    const m = mm / 1000;
    return `${m.toFixed(m >= 10 ? 1 : 2).replace(/\.?0+$/, '')} m`;
  }
  return `${Math.round(mm)} mm`;
}

const EMPTY_SEL: Selection3D = {
  kind: 'none',
  count: 0,
  label: '',
  isLeg: false,
  legDrivable: false,
  legHidden: false,
  model3: '',
  levelId: '',
  elevation: 0,
  moved: false,
  rerouted: false,
  bends: 0,
  isPipe3: false,
  bend: 'long',
  shownIn3D: true,
  hidden: false,
  noStruct: false,
  runLength: 0,
  isDim: false,
  dimText: '',
  dimLength: 0,
  dimFlip: false,
  isZone: false,
  zoneMode: 'floor',
  wallSide: 'N',
  wallH: 0,
  pipeColor: '',
  pipeColorOwn: false,
  flowPinned: 'auto',
  flowMedium: 'none',
  flowReversed: false,
  rate: 0,
  rateReadout: '',
  shapeId: '',
};

/**
 * What a selected run can be told to carry. `Auto` is first and is the resting
 * state: it is not a fourth medium but a refusal to pin one, so the run keeps
 * following the drawing.
 */
const FLOW_CHOICES: { id: FlowMedium | 'auto'; label: string; title: string }[] = [
  { id: 'auto', label: 'Auto', title: '' },
  { id: 'material', label: 'Material', title: 'Always product, whatever the drawing says' },
  { id: 'air', label: 'Air', title: 'Always air, whatever the drawing says' },
  { id: 'none', label: 'None', title: 'Never animate this run — a tapping, a drain, a spare' },
];

/**
 * Hand the legend swatch its stream's colour, so the two live in one place
 * rather than being kept in step by hand between here and the stylesheet.
 */
const inkVar = (hex: string) => ({ '--ink': hex }) as CSSProperties;

/**
 * Service colours, roughly as a plant marks its lines up. Offered as swatches
 * because picking "the green one" is faster and more consistent than mixing a
 * green, and because a drawing where every line is a slightly different green
 * is worse than one with eight honest colours.
 */
const PIPE_COLOURS: { hex: string; name: string }[] = [
  { hex: '#1f8a4c', name: 'Water' },
  { hex: '#5aa8dd', name: 'Compressed air' },
  { hex: '#b9c0c8', name: 'Steam' },
  { hex: '#d9a520', name: 'Gas' },
  { hex: '#8a5a2b', name: 'Oil' },
  { hex: '#7a4fa0', name: 'Acid / alkali' },
  { hex: '#e0682c', name: 'Product' },
  { hex: '#c0261f', name: 'Fire / safety' },
];

interface Props {
  /**
   * Open the 3D PDF dialog. Hosted by AppShell beside its 2D sibling so both
   * modals sit above the view overlays rather than inside one of them.
   */
  onExportPdf: () => void;
}

export function Canvas3DView({ onExportPdf }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<View3DStats>({
    shapes: 0,
    pipes: 0,
    triangles: 0,
    modelled: 0,
    fps: 0,
    levels: 0,
  });
  const [proj, setProj] = useState(engine3d.getProjection());
  const [flags, setFlags] = useState({ ...engine3d.opts, structure: true });
  const [preset, setPreset] = useState<ViewPreset>('isoSW');
  const [hint, setHint] = useState(true);
  const [sel, setSel] = useState<Selection3D>(EMPTY_SEL);
  const [levels, setLevels] = useState<Level[]>([]);
  const [levelsOpen, setLevelsOpen] = useState(false);
  const [tool, setToolState] = useState<'select' | 'pipe' | 'dim'>('select');
  const [dimPending, setDimPending] = useState(false);
  /** Local mirror of the selected dimension's text, so typing stays smooth. */
  const [dimText, setDimText] = useState('');
  const [dimId, setDimId] = useState('');
  const [axis, setAxis] = useState<AxisMode>('auto');
  const [newBend, setNewBend] = useState<BendStyle>('long');
  const [draftLen, setDraftLen] = useState(-1);
  /** Live length of the leg under the cursor, and of the whole run, in mm. */
  const [draftLeg, setDraftLeg] = useState(-1);
  const [draftTotal, setDraftTotal] = useState(-1);
  const [continuing, setContinuing] = useState(false);
  const [hiddenN, setHiddenN] = useState(0);
  /** Leg figures the engineer has switched off, across the drawing. */
  const [hiddenDims, setHiddenDims] = useState(0);
  const [scaleMm, setScaleMm] = useState(25);
  const [editing, setEditing] = useState<string | null>(null);
  /** Whether the plant is running, and how fast. Session-only, like Isolate. */
  const [flowOn, setFlowOn] = useState(false);
  const [flowSpeed, setFlowSpeed] = useState(1);
  /** Local mirror of the selected item's rate, so typing is not fought by the poll. */
  const [rateText, setRateText] = useState('');
  const [rateId, setRateId] = useState('');
  /** Whether the charge-material popover is showing under the selection bar. */
  const [feedOpen, setFeedOpen] = useState(false);
  /**
   * Bumped on every document change, to re-read the selected shape's feed.
   * The selection bar polls the engine; the feed editor holds React state, so
   * it needs telling when an undo has rewritten what it is showing.
   */
  const [feedVer, setFeedVer] = useState(0);

  /** Pull everything the HUD mirrors out of the engine in one go. */
  const sync = useCallback(() => {
    setSel(engine3d.getSelection3D());
    setLevels([...engine3d.getLevels()]);
    setToolState(engine3d.getTool());
    setAxis(engine3d.getAxisMode());
    setNewBend(engine3d.getNewBend());
    setDraftLen(engine3d.draftLength());
    setDraftLeg(engine3d.draftLegMm());
    setDraftTotal(engine3d.draftTotalMm());
    setContinuing(engine3d.continuingRun());
    setHiddenN(engine3d.hiddenCount());
    setScaleMm(engine3d.getScaleMm());
    setDimPending(engine3d.dimPending());
    setHiddenDims(engine3d.hiddenLegDims());
    // Mirrored rather than owned here: R toggles the flow from the keyboard too.
    setFlowOn(engine3d.isFlowRunning());
    setFeedVer((v) => v + 1);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    engine3d.mount(canvas, wrap);
    // The engine is constructed before anyone has signed in, so pick up THIS
    // account's remembered toggles now that we know who it is.
    engine3d.reloadViewPrefs();
    engine3d.setPreset('isoSW');
    engine3d.fitView();
    setProj(engine3d.getProjection());
    setFlags({ ...engine3d.opts, structure: engine3d.isStructureOn() });
    sync();

    // Selection and document changes come through the existing engine bus, so
    // the HUD stays in step with edits made from either view.
    const offSel = engine.on('selection', sync);
    const offDoc = engine.on('doc', sync);
    // The statistics only have to feel live, not be frame-accurate — polling
    // beats re-rendering React on every one of sixty frames a second.
    const timer = window.setInterval(() => setStats(engine3d.getStats()), 400);
    // The tool and the run in progress change from the keyboard too, so mirror
    // them on the same cheap tick rather than wiring an event for each.
    const toolTimer = window.setInterval(sync, 180);
    const offFrame = engine3d.onFrame(sync);
    const dismiss = () => setHint(false);
    canvas.addEventListener('pointerdown', dismiss, { once: true });
    canvas.addEventListener('wheel', dismiss, { once: true, passive: true });
    return () => {
      offSel();
      offDoc();
      window.clearInterval(timer);
      window.clearInterval(toolTimer);
      offFrame();
      canvas.removeEventListener('pointerdown', dismiss);
      canvas.removeEventListener('wheel', dismiss);
      engine3d.unmount();
    };
  }, [sync]);

  const applyPreset = useCallback((p: ViewPreset) => {
    engine3d.setPreset(p);
    setPreset(p);
    setProj(engine3d.getProjection());
  }, []);

  const toggle = useCallback(
    (
      k:
        | 'grid'
        | 'shadows'
        | 'labels'
        | 'tags'
        | 'floors'
        | 'dims'
        | 'dimsSelectedOnly'
        | 'isolate',
    ) => {
      engine3d.toggle(k);
      setFlags((f) => ({ ...f, ...engine3d.opts }));
    },
    [],
  );

  const toggleStructure = useCallback(() => {
    const on = engine3d.toggleStructure();
    setFlags((f) => ({ ...f, structure: on }));
  }, []);

  const setProjection = useCallback((k: 'perspective' | 'ortho') => {
    engine3d.setProjection(k);
    setProj(k);
  }, []);

  // Re-seed the text box only when a DIFFERENT dimension is selected, so typing
  // into it is never interrupted by the engine's own polling. The identity has
  // to be something the typing cannot change: the caption used to be part of it,
  // which meant every keystroke looked like a new selection.
  const selDimId = sel.isDim ? engine3d.selectedDimKey() : '';
  useEffect(() => {
    if (selDimId !== dimId) {
      setDimId(selDimId);
      setDimText(sel.dimText);
    }
  }, [selDimId, dimId, sel.dimText]);

  const selRateId = engine3d.selectedKey();
  useEffect(() => {
    if (selRateId !== rateId) {
      setRateId(selRateId);
      setRateText(sel.rate ? String(sel.rate) : '');
    }
  }, [selRateId, rateId, sel.rate]);

  const hasSel = sel.kind !== 'none';
  /* The selected equipment item, for the charge-material popover. Read fresh
     rather than mirrored into state: the popover edits it directly, and a stale
     copy would show a material list one edit behind what the balance is using. */
  const feedShape = sel.shapeId ? engine.getShape(sel.shapeId) : undefined;
  /* Re-read on the HUD's own poll (feedVer), so a percentage typed in the
     Formulation tab shows up in the legend without a second mechanism. */
  const recipes = engine.getRecipes();
  const feedCount = feedOf(feedShape).length;
  // Why a leg's length is refused, so the panel can say so rather than just
  // greying the box out.
  const legWhy = sel.isLeg && !sel.legDrivable ? engine3d.legBlockedReason() : '';

  return (
    <div id="view3d" ref={wrapRef}>
      <canvas id="view3d-canvas" ref={canvasRef} />

      {stats.shapes === 0 && (
        <div className="v3-empty">
          <div>Nothing to show in 3D yet</div>
          <small>Switch back to 2D, place some equipment, then return here.</small>
        </div>
      )}

      {/* ── Viewpoints + levels ── */}
      <div className="v3-hud v3-tl">
        <div className="v3-col">
          <div className="v3-group">
            <span className="v3-lbl v3-optional">View</span>
            {PRESETS.map((p) => (
              <button
                key={p.id}
                className={'v3-btn' + (preset === p.id ? ' active' : '')}
                title={p.title}
                onClick={() => applyPreset(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="v3-group">
            <span className="v3-lbl v3-optional">Tool</span>
            <button
              className={'v3-btn' + (tool === 'select' ? ' active' : '')}
              title="Select and move things (Esc)"
              onClick={() => {
                engine3d.setTool('select');
                sync();
              }}
            >
              ▶ Select
            </button>
            <button
              className={'v3-btn' + (tool === 'pipe' ? ' active' : '')}
              title="Draw pipe: click a nozzle, another pipe, or open space (P)"
              onClick={() => {
                engine3d.setTool('pipe');
                sync();
              }}
            >
              ⌇ Pipe
            </button>
            <button
              className={'v3-btn' + (tool === 'dim' ? ' active' : '')}
              title="Dimension: click a start point, then an end point, then type its text (D)"
              onClick={() => {
                engine3d.setTool('dim');
                sync();
              }}
            >
              ⟺ Dimension
            </button>
          </div>

          {tool === 'pipe' && (
            <div className="v3-group">
              <span className="v3-lbl v3-optional">Leg</span>
              {(['auto', 'horizontal', 'vertical', 'diagonal'] as AxisMode[]).map((m) => (
                <button
                  key={m}
                  className={'v3-btn' + (axis === m ? ' active' : '')}
                  title={
                    m === 'auto'
                      ? 'Follow whichever way you drag'
                      : m === 'horizontal'
                        ? 'Lock every new leg flat'
                        : m === 'vertical'
                          ? 'Lock every new leg vertical'
                          : 'Free sloped legs — no axis lock'
                  }
                  onClick={() => {
                    engine3d.setAxisMode(m);
                    sync();
                  }}
                >
                  {m === 'auto'
                    ? 'Auto'
                    : m === 'horizontal'
                      ? '↔ Horiz'
                      : m === 'vertical'
                        ? '↕ Vert'
                        : '⟋ Diag'}
                </button>
              ))}
              <span className="v3-sep" />
              <span className="v3-lbl v3-optional">Elbow</span>
              {(['long', 'short', 'mitre'] as BendStyle[]).map((bnd) => (
                <button
                  key={bnd}
                  className={'v3-btn' + (newBend === bnd ? ' active' : '')}
                  title={
                    bnd === 'long'
                      ? 'Long-radius bends'
                      : bnd === 'short'
                        ? 'Short-radius bends'
                        : 'Mitred (square) corners'
                  }
                  onClick={() => {
                    engine3d.setNewBend(bnd);
                    sync();
                  }}
                >
                  {bnd === 'long' ? 'Long' : bnd === 'short' ? 'Short' : 'Mitre'}
                </button>
              ))}
            </div>
          )}

          <div className="v3-group v3-stack v3-optional">
            <div className="v3-row">
              <button
                className={'v3-btn' + (levelsOpen ? ' active' : '')}
                title="Plant floors — add the ones your building has; equipment stands on one of them"
                onClick={() => setLevelsOpen((v) => !v)}
              >
                {levelsOpen ? '▾' : '▸'} Levels ({levels.length})
              </button>
              <button
                className="v3-btn"
                title="Replace the floors with ones derived from the equipment types and how they are connected, then put everything on the right one. Overwrites the floors you have added — undoable"
                onClick={() => {
                  engine3d.arrangeByLevel();
                  sync();
                }}
              >
                ⇅ Arrange
              </button>
            </div>

            {levelsOpen && (
              <div className="v3-levels">
                {/* Top floor first, the way an elevation is drawn. */}
                {[...levels].reverse().map((l) => (
                  <div key={l.id} className={'v3-level' + (sel.levelId === l.id ? ' active' : '')}>
                    {editing === l.id ? (
                      <input
                        className="v3-lname"
                        autoFocus
                        defaultValue={l.name}
                        onBlur={(e) => {
                          engine3d.renameLevel(l.id, e.target.value.trim() || l.name);
                          setEditing(null);
                          sync();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
                        }}
                      />
                    ) : (
                      <span
                        className="v3-lname"
                        title="Rename"
                        onDoubleClick={() => setEditing(l.id)}
                      >
                        {l.name}
                      </span>
                    )}
                    <input
                      className="v3-lelev"
                      type="number"
                      step={10}
                      value={l.elevation}
                      title="Floor height"
                      onChange={(e) => {
                        engine3d.setLevelElevation(l.id, parseFloat(e.target.value) || 0);
                        sync();
                      }}
                    />
                    <button
                      className={'v3-ico' + (l.structure === false ? '' : ' on')}
                      title={
                        l.structure === false
                          ? 'Steelwork hidden for this floor — click to show'
                          : 'Steelwork shown for this floor — click to hide'
                      }
                      disabled={levels[0]?.id === l.id}
                      onClick={() => {
                        engine3d.setLevelStructure(l.id, l.structure === false);
                        sync();
                      }}
                    >
                      ▤
                    </button>
                    <button
                      className="v3-x"
                      title="Delete this floor (anything on it drops to the one below)"
                      disabled={levels.length <= 1}
                      onClick={() => {
                        engine3d.removeLevel(l.id);
                        sync();
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  className="v3-btn v3-add"
                  title="Add a floor above the top one — rename it and set its height, then put equipment on it from the selection bar"
                  onClick={() => {
                    engine3d.addLevel();
                    sync();
                  }}
                >
                  ＋ Add floor
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Projection + display toggles ── */}
      <div className="v3-hud v3-tr">
        {/* First, and on its own: this is the one control here that makes the
            plant do something rather than changing how it is drawn. */}
        <div className="v3-group">
          <button
            className={'v3-btn v3-run' + (flowOn ? ' active' : '')}
            title={
              flowOn
                ? 'Stop the flow — the material and air stay where they are (R)'
                : 'Run the plant: material and air travelling through the pipework (R)'
            }
            onClick={() => setFlowOn(engine3d.toggleFlow())}
          >
            {flowOn ? '❚❚ Stop the Flow' : '▶ Run the Flow'}
          </button>
          {flowOn && (
            <>
              <span className="v3-sep" />
              {/* The legend moves at each stream's own rhythm, so it reads as a
                  sample of the pipework rather than as two coloured squares. */}
              {!recipes.length && (
                <span className="v3-key" title="Product: long slugs, unhurried">
                  <i className="v3-key-bar material" style={inkVar(FLOW_INK.material)} />
                  Material
                </span>
              )}
              <span className="v3-key" title="Air: short streaks, nearly three times the speed">
                <i className="v3-key-bar air" style={inkVar(FLOW_INK.air)} />
                Air
              </span>
              <span className="v3-sep" />
              <div className="v3-slider">
                <span className="v3-lbl" style={{ padding: 0 }}>
                  Speed
                </span>
                <input
                  type="range"
                  min={0.25}
                  max={3}
                  step={0.25}
                  value={flowSpeed}
                  title="How fast both streams run — material stays slower than air at every setting"
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    engine3d.setFlowSpeed(v);
                    setFlowSpeed(v);
                  }}
                />
              </div>
            </>
          )}
        </div>
        <div className="v3-group">
          <button
            className={'v3-btn' + (proj === 'perspective' ? ' active' : '')}
            title="Perspective — how the plant actually looks"
            onClick={() => setProjection('perspective')}
          >
            Perspective
          </button>
          <button
            className={'v3-btn' + (proj === 'ortho' ? ' active' : '')}
            title="Isometric — parallel lines stay parallel, equal lengths measure equal"
            onClick={() => setProjection('ortho')}
          >
            Isometric
          </button>
        </div>
        <div className="v3-group">
          <button
            className={'v3-btn' + (flags.isolate ? ' active' : '')}
            title="Ghost everything except the selection, so a crowded drawing gets out of the way (I)"
            onClick={() => toggle('isolate')}
          >
            ◍ Isolate
          </button>
          <button
            className={'v3-btn' + (flags.structure ? ' active' : '')}
            title="Access decks, columns, handrails and stairs"
            onClick={toggleStructure}
          >
            Structure
          </button>
          <button
            className={'v3-btn' + (flags.shadows ? ' active' : '')}
            title="Ground shadows"
            onClick={() => toggle('shadows')}
          >
            Shadows
          </button>
          <button
            className={'v3-btn' + (flags.floors ? ' active' : '')}
            title="Floor names and heights, marked down the near edge"
            onClick={() => toggle('floors')}
          >
            Floors
          </button>
          <button
            className={'v3-btn' + (flags.grid ? ' active' : '')}
            title="Floor grid"
            onClick={() => toggle('grid')}
          >
            Grid
          </button>
          <button
            className={'v3-btn' + (flags.labels ? ' active' : '')}
            title="Equipment labels"
            onClick={() => toggle('labels')}
          >
            Labels
          </button>
          <button
            className={'v3-btn' + (flags.tags ? ' active' : '')}
            title="P&ID tags"
            onClick={() => toggle('tags')}
          >
            Tags
          </button>
        </div>
        {/* Lives up here with the other visibility controls, where a wide
            selection bar cannot reach over it. */}
        {hiddenN > 0 && (
          <button
            className="v3-btn v3-hidden-chip"
            title="Bring every hidden item back into the 3D view"
            onClick={() => {
              engine3d.showAllHidden();
              sync();
            }}
          >
            👁 {hiddenN} hidden — show all
          </button>
        )}
        <div className="v3-group">
          <button
            className={'v3-btn' + (flags.dims ? ' active' : '')}
            title="Dimension every straight pipe leg, set off to the side of the run"
            onClick={() => toggle('dims')}
          >
            ⟺ Dims
          </button>
          {flags.dims && (
            <>
              <button
                className={'v3-btn' + (flags.dimsSelectedOnly ? ' active' : '')}
                title={
                  flags.dimsSelectedOnly
                    ? 'Only the selected run is dimensioned — click for all of them'
                    : 'Every run is dimensioned — click for the selected one only'
                }
                onClick={() => toggle('dimsSelectedOnly')}
              >
                {flags.dimsSelectedOnly ? 'Selected' : 'All'}
              </button>
              {hiddenDims > 0 && (
                <button
                  className="v3-btn"
                  title="Dimension every leg again, keeping the captions you typed"
                  onClick={() => {
                    engine3d.showLegDims();
                    sync();
                  }}
                >
                  ↺ {hiddenDims} hidden
                </button>
              )}
              <span className="v3-sep" />
              <span className="v3-lbl v3-optional">1 unit</span>
              <input
                className="v3-lelev"
                type="number"
                min={0.1}
                step={1}
                value={scaleMm}
                title="Millimetres per drawing unit — what the dimension figures are computed in"
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (Number.isFinite(v)) {
                    engine3d.setScaleMm(v);
                    setScaleMm(v);
                  }
                }}
              />
              <span className="v3-lbl">mm</span>
            </>
          )}
        </div>
      </div>

      {/* ── Selection inspector ── */}
      {hasSel && (
        /* The selection bar normally sits below the keyboard hint. With the
           materials panel open it has to sit above it, or the panel opens
           underneath the hint and half of it cannot be read. */
        <div className={'v3-hud v3-bc' + (feedOpen ? ' v3-over-hint' : '')}>
          <div className="v3-group v3-sel">
            <span className="v3-selname" title={sel.label}>
              {sel.label}
            </span>

            {(sel.kind === 'shape' || sel.kind === 'mixed') && (
              <>
                <span className="v3-sep" />
                <span className="v3-lbl">Level</span>
                <select
                  className="v3-select"
                  value={sel.levelId}
                  title="Which floor this equipment stands on"
                  onChange={(e) => {
                    engine3d.setSelectionLevel(e.target.value);
                    sync();
                  }}
                >
                  {!sel.levelId && <option value="">— mixed —</option>}
                  {[...levels].reverse().map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} ({l.elevation})
                    </option>
                  ))}
                </select>
                <button
                  className="v3-btn"
                  title="Lower by 10"
                  onClick={() => {
                    engine3d.nudgeSelection(-10);
                    sync();
                  }}
                >
                  ▼
                </button>
                <span className="v3-elev" title="Height above ground">
                  {sel.elevation}
                </span>
                <button
                  className="v3-btn"
                  title="Raise by 10"
                  onClick={() => {
                    engine3d.nudgeSelection(10);
                    sync();
                  }}
                >
                  ▲
                </button>
                {sel.moved && (
                  <button
                    className="v3-btn v3-warn"
                    title="Discard the 3D position and follow the 2D drawing again"
                    onClick={() => {
                      engine3d.resetSelectionPlan();
                      sync();
                    }}
                  >
                    ↺ 2D position
                  </button>
                )}
                <span className="v3-sep" />
                <button
                  className={'v3-btn' + (sel.noStruct ? '' : ' active')}
                  title={
                    sel.noStruct
                      ? 'No platform under this item — click to build one'
                      : 'Platform built under this item — click to suppress it'
                  }
                  onClick={() => {
                    engine3d.toggleSelectionStructure();
                    sync();
                  }}
                >
                  ▤ Platform
                </button>
                <span className="v3-sep" />
                <span className="v3-lbl v3-optional">Model</span>
                <select
                  className="v3-select"
                  value={sel.model3}
                  title={
                    sel.model3
                      ? 'This item is pinned to a 3D model, and stays pinned if you rename it'
                      : 'The 3D shape is being guessed from the symbol and its name — pick one to fix it in place'
                  }
                  onChange={(e) => {
                    engine3d.setSelectionModel(e.target.value);
                    sync();
                  }}
                >
                  <option value="">Auto (from name)</option>
                  {MODEL_CHOICES.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <button
                  className="v3-btn v3-warn"
                  title="Hide this equipment in 3D — the 2D drawing keeps it"
                  onClick={() => {
                    engine3d.hideSelectedShapes();
                    sync();
                  }}
                >
                  ✕ Hide in 3D
                </button>
              </>
            )}

            {sel.isZone && (
              <>
                <span className="v3-sep" />
                <span className="v3-lbl v3-optional">Show as</span>
                {(['floor', 'wall', 'room'] as const).map((m) => (
                  <button
                    key={m}
                    className={'v3-btn' + (sel.zoneMode === m ? ' active' : '')}
                    title={
                      m === 'floor'
                        ? 'A painted area on the ground'
                        : m === 'wall'
                          ? 'Stand one edge up as a wall — a tower face for a riser to run against'
                          : 'A low wall all the way round, as an enclosure'
                    }
                    onClick={() => {
                      engine3d.setZoneMode(m);
                      sync();
                    }}
                  >
                    {m === 'floor' ? '▭ Floor' : m === 'wall' ? '▯ Wall' : '⬚ Room'}
                  </button>
                ))}
                {sel.zoneMode === 'wall' && (
                  <>
                    <span className="v3-sep" />
                    <span className="v3-lbl v3-optional">Edge</span>
                    {(['N', 'E', 'S', 'W'] as PortKey[]).map((side) => (
                      <button
                        key={side}
                        className={'v3-btn' + (sel.wallSide === side ? ' active' : '')}
                        title={`Stand the wall on the ${side} edge`}
                        onClick={() => {
                          engine3d.setWallSide(side);
                          sync();
                        }}
                      >
                        {side}
                      </button>
                    ))}
                  </>
                )}
                {sel.zoneMode !== 'floor' && (
                  <>
                    <span className="v3-sep" />
                    <span className="v3-lbl v3-optional">Height</span>
                    <input
                      className="v3-lelev"
                      type="number"
                      step={20}
                      value={sel.wallH}
                      title="How tall it stands"
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (Number.isFinite(v)) {
                          engine3d.setWallHeight(v);
                          sync();
                        }
                      }}
                    />
                  </>
                )}
              </>
            )}

            {sel.isDim && (
              <>
                <span className="v3-sep" />
                <input
                  className="v3-dimtext"
                  placeholder={formatMm(sel.dimLength * scaleMm)}
                  value={dimText}
                  title="What this dimension says. Leave it empty to show the measured length."
                  onChange={(e) => {
                    setDimText(e.target.value);
                    engine3d.setDimText(e.target.value);
                  }}
                />
                <button
                  className="v3-btn"
                  title="Use the measured length again"
                  disabled={dimText === ''}
                  onClick={() => {
                    setDimText('');
                    engine3d.setDimText('');
                    sync();
                  }}
                >
                  ↺
                </button>
                <span className="v3-sep" />
                <span className="v3-lbl v3-optional">Length</span>
                <input
                  className="v3-lelev"
                  type="number"
                  step={100}
                  value={Math.round(sel.dimLength * scaleMm)}
                  disabled={sel.isLeg && !sel.legDrivable}
                  title={
                    sel.isLeg
                      ? sel.legDrivable
                        ? 'Type a length and the pipe moves to match — everything past this leg slides along it'
                        : 'This leg cannot be driven: ' + legWhy
                      : 'Set the length and the far end moves to match, along the line it already points down'
                  }
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (Number.isFinite(v) && v > 0) {
                      engine3d.setDimLength(v);
                      sync();
                    }
                  }}
                />
                <span className="v3-lbl">mm</span>
                {sel.isLeg && !sel.legDrivable && <span className="v3-why">{legWhy}</span>}
                {/* Side and offset are properties of a PLACED dimension. A leg
                    figure is drawn by the automatic pass, which sites itself. */}
                {!sel.isLeg && (
                  <>
                    <span className="v3-sep" />
                    <button
                      className={'v3-btn' + (sel.dimFlip ? ' active' : '')}
                      title="Put it on the other side"
                      onClick={() => {
                        engine3d.flipDim();
                        sync();
                      }}
                    >
                      ⇅ Side
                    </button>
                    <button
                      className="v3-btn"
                      title="Closer to what it measures"
                      onClick={() => engine3d.nudgeDimOffset(-10)}
                    >
                      −
                    </button>
                    <button
                      className="v3-btn"
                      title="Further away"
                      onClick={() => engine3d.nudgeDimOffset(10)}
                    >
                      +
                    </button>
                  </>
                )}
                <span className="v3-sep" />
                {/* Clearing the caption and deleting the dimension are different
                    things — one restores the measurement, the other takes the
                    figure off the drawing — so they are not the same button. */}
                {sel.isLeg && (
                  <button
                    className="v3-btn"
                    title="Clear the caption and show the measured length again"
                    disabled={dimText === ''}
                    onClick={() => {
                      setDimText('');
                      engine3d.clearLegCaption();
                      sync();
                    }}
                  >
                    ↺ Caption
                  </button>
                )}
                <button
                  className="v3-btn v3-warn"
                  title={
                    sel.isLeg
                      ? 'Stop dimensioning this leg (Del) — the pipe is untouched, and Show all brings it back'
                      : 'Delete this dimension (Del)'
                  }
                  onClick={() => {
                    engine3d.deleteSelectedDims();
                    sync();
                  }}
                >
                  ✕ Delete
                </button>
              </>
            )}

            {(sel.kind === 'conn' || sel.kind === 'mixed') && (
              <>
                <span className="v3-sep" />
                <span className="v3-lbl">Run</span>
                <span className="v3-lbl">Colour</span>
                <div className="v3-swatches">
                  {PIPE_COLOURS.map((c) => (
                    <button
                      key={c.hex}
                      className={'v3-sw' + (sel.pipeColor.toLowerCase() === c.hex ? ' active' : '')}
                      style={{ background: c.hex }}
                      title={c.name}
                      onClick={() => {
                        engine3d.setPipeColor(c.hex);
                        sync();
                      }}
                    />
                  ))}
                </div>
                <input
                  className="v3-pick"
                  type="color"
                  value={sel.pipeColor || '#9ea6b0'}
                  title="Any other colour"
                  onChange={(e) => {
                    engine3d.setPipeColor(e.target.value);
                    sync();
                  }}
                />
                {sel.pipeColorOwn && (
                  <button
                    className="v3-btn v3-warn"
                    title="Back to bare steel, or to the 2D connector's colour"
                    onClick={() => {
                      engine3d.resetPipeColor();
                      sync();
                    }}
                  >
                    ↺
                  </button>
                )}
                <span className="v3-sep" />
                <span className="v3-lbl v3-optional">Flow</span>
                {FLOW_CHOICES.map((c) => (
                  <button
                    key={c.id}
                    className={'v3-btn' + (sel.flowPinned === c.id ? ' active' : '')}
                    title={
                      c.id === 'auto'
                        ? `Read it off the drawing — this run comes out as ${FLOW_NAMES[sel.flowMedium].toLowerCase()}`
                        : c.title
                    }
                    onClick={() => {
                      engine3d.setSelectionFlow(c.id);
                      sync();
                    }}
                  >
                    {c.label}
                  </button>
                ))}
                {sel.flowPinned === 'auto' && (
                  <span className="v3-elev" title="What the drawing implies">
                    {FLOW_NAMES[sel.flowMedium]}
                  </span>
                )}
                <button
                  className={'v3-btn' + (sel.flowReversed ? ' active' : '')}
                  title={
                    sel.flowReversed
                      ? 'Turned round — flowing back toward the end this was drawn from. Click to put it back'
                      : 'Flowing the way it was drawn. Click to turn it round, the way a vacuum line runs'
                  }
                  onClick={() => {
                    engine3d.reverseSelectionFlow(!sel.flowReversed);
                    sync();
                  }}
                >
                  {sel.flowReversed ? '⇤ Reversed' : '⇥ As drawn'}
                </button>
                <span className="v3-sep" />
                <button
                  className="v3-btn"
                  title="Add a bend — drag its arrows to build horizontal and vertical legs"
                  onClick={() => {
                    engine3d.addBendToSelection();
                    sync();
                  }}
                >
                  ＋ Bend
                </button>
                <button
                  className="v3-btn"
                  title="Remove the last bend"
                  disabled={sel.bends === 0}
                  onClick={() => {
                    engine3d.removeBendFromSelection();
                    sync();
                  }}
                >
                  − Bend
                </button>
                {sel.bends > 0 && <span className="v3-elev">{sel.bends}</span>}
                {sel.bends === 0 && (
                  <button
                    className="v3-btn"
                    title="Turn along the other axis first"
                    onClick={() => {
                      engine3d.flipPipeAxis();
                      sync();
                    }}
                  >
                    ⤢ Flip turn
                  </button>
                )}
                {sel.rerouted && !sel.isPipe3 && (
                  <button
                    className="v3-btn v3-warn"
                    title="Back to automatic routing"
                    onClick={() => {
                      engine3d.resetPipeRoute();
                      sync();
                    }}
                  >
                    ↺ Auto route
                  </button>
                )}
                {sel.runLength > 0 && (
                  <>
                    <span className="v3-sep" />
                    <span className="v3-lbl v3-optional">Length</span>
                    <span className="v3-elev" title="Developed length along the centre-line">
                      {formatMm(sel.runLength * scaleMm)}
                    </span>
                  </>
                )}
                <span className="v3-sep" />
                <span className="v3-lbl v3-optional">Elbow</span>
                {(['long', 'short', 'mitre'] as BendStyle[]).map((bnd) => (
                  <button
                    key={bnd}
                    className={'v3-btn' + (sel.bend === bnd ? ' active' : '')}
                    onClick={() => {
                      engine3d.setSelectionBend(bnd);
                      sync();
                    }}
                  >
                    {bnd === 'long' ? 'Long' : bnd === 'short' ? 'Short' : 'Mitre'}
                  </button>
                ))}
                <span className="v3-sep" />
                {sel.shownIn3D ? (
                  <button
                    className="v3-btn v3-warn"
                    title={
                      sel.isPipe3
                        ? 'Delete this pipe (Del)'
                        : 'Hide this pipe in 3D — the 2D connector is kept (Del)'
                    }
                    onClick={() => {
                      engine3d.deleteSelectedPipes();
                      sync();
                    }}
                  >
                    {sel.isPipe3 ? '✕ Delete' : '✕ Hide in 3D'}
                  </button>
                ) : (
                  <button
                    className="v3-btn"
                    title="Show this connector's pipe in 3D again"
                    onClick={() => {
                      engine3d.showSelectedPipes();
                      sync();
                    }}
                  >
                    ↺ Show in 3D
                  </button>
                )}
              </>
            )}

            {!sel.isDim && !sel.isLeg && (
              <>
                <span className="v3-sep" />
                <span className="v3-lbl v3-optional">{sel.kind === 'shape' ? 'Feed' : 'Rate'}</span>
                <input
                  className="v3-lelev"
                  type="number"
                  min={0}
                  step={10}
                  placeholder="kg/h"
                  value={rateText}
                  title={
                    sel.kind === 'shape'
                      ? 'Material this item feeds into the line, in kg/h. Whatever arrives from upstream is carried through on top of it, so a machine fed from above normally leaves this empty.'
                      : 'Pin this run to a throughput. Left empty, the balance divides what arrives evenly between the runs leaving that machine — pin the odd one out, such as the fines off a cyclone, and the rest share what is left.'
                  }
                  onChange={(e) => {
                    setRateText(e.target.value);
                    engine3d.setSelectionRate(parseFloat(e.target.value));
                  }}
                />
                {sel.rateReadout && (
                  <span
                    className="v3-elev v3-rate"
                    title={
                      sel.kind === 'shape' ? 'What passes through it' : 'What this run carries'
                    }
                  >
                    {sel.rateReadout}
                  </span>
                )}
              </>
            )}

            {/* What this item charges in. The same editor as the 2D properties
                panel — a feeder doses the same material whichever view you
                happen to be looking at it in. */}
            {sel.kind === 'shape' && sel.shapeId && (
              <>
                <span className="v3-sep" />
                <button
                  className={'v3-btn' + (feedOpen ? ' active' : '')}
                  title="Pick the raw materials this charging unit or feeder doses, and name the finished good if the line's product starts here"
                  onClick={() => setFeedOpen((v) => !v)}
                >
                  ⚗ Materials
                  {feedCount > 0 && ` (${feedCount})`}
                </button>
              </>
            )}

            <span className="v3-sep" />
            <button
              className="v3-btn"
              title="Frame the selection"
              onClick={() => engine3d.fitSelection()}
            >
              Focus
            </button>
          </div>

          {feedOpen && feedShape && (
            <div className="fe-pop">
              <div className="fe-pop-hdr">
                ⚗ Materials charged at {feedShape.label || feedShape.tag || feedShape.type}
                <button className="fe-pop-close" title="Close" onClick={() => setFeedOpen(false)}>
                  ✕
                </button>
              </div>
              <FeedEditor shape={feedShape} version={feedVer} />
              <div className="fe-fg">
                <label htmlFor="v3-fgstart">FG starts</label>
                <select
                  id="v3-fgstart"
                  value={
                    feedShape.fgStart === undefined ? 'auto' : feedShape.fgStart ? 'on' : 'off'
                  }
                  title="Where the recipe ends and the product begins. The extruder or kneader does this on its own — that is where the compound is made."
                  onChange={(e) => {
                    engine.setFgStart(e.target.value as 'auto' | 'on' | 'off');
                    sync();
                  }}
                >
                  <option value="auto">
                    Auto{FG_START_TYPES.has(feedShape.type) ? ' — yes' : ' — no'}
                  </option>
                  <option value="on">Yes, here</option>
                  <option value="off">No</option>
                </select>
                {isFgStart(feedShape) && (
                  <span className="fe-fg-note">
                    Carried to packing as this line&rsquo;s finished good.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── What is flowing ──
          Only while the plant is running, and only once there is a recipe to
          name: a colour is worth nothing without the thing it stands for. */}
      {flowOn && recipes.length > 0 && (
        <div className="fl-legend">
          {recipes.map((r) => (
            <div className="fl-line" key={r.formulation.id}>
              <div className="fl-fg">
                <i className="fl-sw" style={{ background: FG_COLOUR }} />
                {r.formulation.fg || 'Unnamed line'}
                <b>{formatRate(r.formulation.totalKgh)}</b>
              </div>
              {r.lines.map((l) => (
                <div className="fl-mat" key={l.part} title={l.name}>
                  <i className="fl-sw" style={{ background: l.colour }} />
                  <span className="fl-nm">{shortMaterialName(l.part)}</span>
                  <b>{l.kgh ? formatRate(l.kgh) : '—'}</b>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── Stats ── */}
      <div className="v3-hud v3-bl">
        <div className="v3-stats">
          <span>
            <b>{stats.shapes}</b> equipment
          </span>
          <span>
            <b>{stats.pipes}</b> pipe runs
          </span>
          <span>
            <b>{stats.levels}</b> levels
          </span>
          <span className="v3-optional">
            <b>{stats.triangles.toLocaleString()}</b> triangles
          </span>
          <span className="v3-optional">
            <b>{stats.fps}</b> fps
          </span>
        </div>
      </div>

      {/* ── Camera + height + export ── */}
      <div className="v3-hud v3-br">
        <div className="v3-group v3-optional">
          <div className="v3-slider">
            <span className="v3-lbl" style={{ padding: 0 }}>
              Height
            </span>
            <input
              type="range"
              min={0.5}
              max={2.5}
              step={0.1}
              defaultValue={1}
              title="Vertical exaggeration — makes low equipment easier to read"
              onChange={(e) => engine3d.setHeightScale(parseFloat(e.target.value))}
            />
          </div>
        </div>
        <div className="v3-group">
          <button className="v3-btn" title="Zoom out" onClick={() => engine3d.zoomOut()}>
            −
          </button>
          <button className="v3-btn" title="Zoom in" onClick={() => engine3d.zoomIn()}>
            +
          </button>
          <span className="v3-sep" />
          <button
            className="v3-btn"
            title="Frame the whole plant"
            onClick={() => engine3d.fitView()}
          >
            Fit
          </button>
          <span className="v3-sep" />
          <button
            className="v3-btn"
            title="Export this view as a PNG"
            onClick={() => engine3d.exportPng(engine.getDocMeta().docName)}
          >
            PNG
          </button>
          <button
            className="v3-btn"
            title="Export 3D views as a PDF portfolio — a different document from the 2D layout sheet set"
            onClick={onExportPdf}
          >
            PDF
          </button>
        </div>
      </div>

      {tool === 'dim' ? (
        <div className="v3-hint">
          {dimPending ? (
            <>
              <kbd>Click</kbd> the end point · it snaps to nozzles and pipes · <kbd>Esc</kbd> cancel
            </>
          ) : (
            <>
              <kbd>Click</kbd> a start point, then an end point, then type the text ·{' '}
              <kbd>Click an existing dimension</kbd> to select it, <kbd>Del</kbd> removes it ·{' '}
              <kbd>Right-drag</kbd> orbits · <kbd>Esc</kbd> exits
            </>
          )}
        </div>
      ) : tool === 'pipe' ? (
        <div className="v3-hint">
          {draftLen < 0 ? (
            <>
              <kbd>Click</kbd> a nozzle, an existing pipe, or open space to start ·{' '}
              <kbd>Click the loose end</kbd> of a run to carry on from it · <kbd>Right-drag</kbd>{' '}
              orbits · <kbd>Esc</kbd> exits
            </>
          ) : (
            <>
              {continuing && <b className="v3-continuing">Continuing this run · </b>}
              {draftLeg >= 0 && <span className="v3-measure">{formatMm(draftLeg)}</span>}
              {draftTotal >= 0 && draftLen > 1 && (
                <span className="v3-measure v3-measure-total">{formatMm(draftTotal)} total</span>
              )}
              <b>{draftLen}</b> point{draftLen === 1 ? '' : 's'} · <kbd>Click</kbd> to add a leg ·
              land on a nozzle or pipe to join · <kbd>Enter</kbd> finish · <kbd>Esc</kbd> cancel
            </>
          )}
        </div>
      ) : (
        hint &&
        stats.shapes > 0 && (
          <div className="v3-hint">
            <kbd>Drag</kbd> orbit · <kbd>Shift</kbd>+drag pan · <kbd>Wheel</kbd> zoom ·{' '}
            <kbd>Wheel</kbd> zooms to the cursor · <kbd>Double-click</kbd> flies to an item ·{' '}
            <kbd>Click again</kbd> for what is behind · <kbd>F</kbd> focus · <kbd>I</kbd> isolate ·{' '}
            <kbd>R</kbd> run the flow
          </div>
        )
      )}
    </div>
  );
}
